import {useRefreshMe} from '../../hooks/session/useRefreshMe';
import {useSession} from '../../hooks/session/SessionProvider';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {CircularProgress, IconButton} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {confirmResetPassword, signIn} from 'aws-amplify/auth';
import {ConsoleLogger} from 'aws-amplify/utils';
import Lottie from 'lottie-react';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, Link, useLocation, useOutletContext} from 'react-router-dom';

import {useMixpanelInstance} from '../../analytics/useMixpanelInstance';
import animationSuccess from '../../assets/lottie//animation-success.json';
import {PasswordChecker} from '../../components/passwordchecker/passwordchecker';
import {useForgotPasswordMutation} from '../../hooks/auth/mutations/useForgotPasswordMutation';
import {useUser} from '../../hooks/session/useSessionSelectors';
import {useApiFailureSnackbar} from '../../hooks/useApiFailureSnackbar';
import {useDocumentTitle} from '../../useDocumentTitle';
import {getApiFailure} from '../../utils/apiFailure';
import {HANDLES_API_FAILURE} from '../../utils/mutationMeta';
// import pattern from '../../assets/svg/pattern-lines.svg';
import {useQueryString} from '../../utils/hooks';

const logger = new ConsoleLogger('Reset');

type ResetState = {
  state:
    | 'idle'
    | 'loading'
    | 'error'
    | 'EmailNotFoundException'
    | 'InvalidPasswordException'
    | 'LimitExceededException'
    | 'ExpiredCodeException'
    | 'playinganimationstart'
    | 'playinganimationended';
  codeAsked: boolean;
  email: string;
  code: string;
  newPassword: string;
  showPassword: boolean;
  invalidPassword?: boolean;
  error?: {message: string};
};

export function Reset() {
  const query = useQueryString();
  const {lang} = useOutletContext<{lang: 'de' | 'en' | 'es' | 'fr' | 'it' | 'pt'}>();
  const location = useLocation();

  const [resetState, setResetState] = useState<ResetState>({
    state: 'idle',
    codeAsked: query.get('code') !== null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    email: query.get('email') ?? location.state?.email ?? '',
    code: query.get('code') ?? '',
    newPassword: '',
    showPassword: false,
  });
  const {state, codeAsked, email, error, code, newPassword, showPassword, invalidPassword} = resetState;

  const {t} = useTranslation();

  const sessionUser = useUser();
  const refreshMe = useRefreshMe();
  const {refreshSession} = useSession();
  const navigate = useNavigate();
  const mixpanel = useMixpanelInstance();
  useDocumentTitle(t('signin.documenttitle'));

  useEffect(() => {
    if (sessionUser === undefined) {
      return;
    }
    if (state === 'playinganimationended') {
      if (sessionUser.lnAccessTokenValid !== true) {
        navigate('/auth/linkedin');
      } else {
        navigate('/');
      }
    }
  }, [sessionUser, state]);

  const onResetForm = () => {
    setResetState({
      state: 'idle',
      codeAsked: false,
      email: '',
      code: '',
      newPassword: '',
      showPassword: false,
    });
  };

  // Refus affiché par `onAskResetCode` : compte introuvable sous le champ, le reste en snackbar.
  const forgotPassword = useForgotPasswordMutation(HANDLES_API_FAILURE);
  const notifyApiFailure = useApiFailureSnackbar();

  const onAskResetCode = async () => {
    query.delete('email');
    query.delete('code');
    setResetState((state) => ({...state, state: 'loading', codeAsked: false, error: undefined}));
    try {
      await forgotPassword.mutateAsync({email, locale: lang});
      query.set('email', email);
      setResetState((state) => ({...state, state: 'idle', codeAsked: true, error: undefined}));
      mixpanel.track('password-reset-requested');
    } catch (error) {
      logger.error({error});
      if (
        getApiFailure(error)?.code === 'user_not_found' ||
        (typeof error === 'object' &&
          error !== undefined &&
          error !== null &&
          'message' in error &&
          error.message === 'not found')
      ) {
        setResetState((state) => ({
          ...state,
          state: 'EmailNotFoundException',
          error: {message: t('forgotpassword.emailnotfound')},
        }));
      } else {
        // L'état `error` n'est pas rendu à cette étape : le refus passe par un snackbar.
        notifyApiFailure(error, {fallback: t('apiFailure.generic')});
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        setResetState((state) => ({...state, state: 'error', error: {message: `Unknown error: ${error}`}}));
      }
    }
  };

  const onSubmitNewPassword = async () => {
    setResetState((state) => ({...state, state: 'loading', error: undefined}));
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      const {isSignedIn} = await signIn({username: email, password: newPassword});
      if (isSignedIn) {
        mixpanel.track('password-reset-completed');
        // `login` est émis par LoginRequiredRoute une fois l'utilisateur identifié.
        setResetState((state) => ({...state, state: 'playinganimationstart', error: undefined}));
        await refreshSession();
        await refreshMe();
      } else {
        setResetState((state) => ({
          ...state,
          state: 'error',
          error: {
            message: 'The password change was successful, but we were unable to log you in. Please contact support',
          },
        }));
      }
    } catch (error) {
      if (typeof error === 'object' && error !== undefined && error !== null && 'name' in error) {
        const e = error;
        if (e.name === 'InvalidPasswordException') {
          setResetState((state) => ({
            ...state,
            state: 'InvalidPasswordException',
            invalidPassword: true,
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            error: {message: e.toString()},
          }));
        } else if (error.name === 'LimitExceededException') {
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          setResetState((state) => ({...state, state: 'LimitExceededException', error: {message: e.toString()}}));
        } else if (error.name === 'ExpiredCodeException') {
          setResetState((state) => ({
            ...state,
            state: 'ExpiredCodeException',
            error: {message: t('forgotpassword.expiredcode')},
          }));
        }
      } else {
        logger.error({error});
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        setResetState((state) => ({...state, state: 'error', error: {message: `Unknown error: ${error}`}}));
      }
    }
  };

  const onEmailChange = (email: string) => {
    setResetState((state) => ({...state, state: 'idle', email: email.toLowerCase(), error: undefined}));
  };

  return (
    <>
      <Box pt={3} px={3}>
        <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('signin.forgot.title')}</Typography>
        <Typography color="secondary" sx={{fontWeight: 700, fontSize: '14px'}}>
          {t('signin.forgot.subtitle')}
        </Typography>
      </Box>
      <Box p={3}>
        <Box component="form" role="form">
          <Box mt={3}>
            {state === 'playinganimationstart' || state === 'playinganimationended' ? (
              <Lottie
                autoplay
                animationData={animationSuccess}
                initialSegment={[0, 100]}
                loop={false}
                onComplete={() => {
                  setResetState((state) => ({...state, state: 'playinganimationended'}));
                }}
                style={{height: 250}}
              />
            ) : (
              <>
                <Box mb={2}>
                  <TextField
                    type="email"
                    autoComplete="username"
                    required
                    fullWidth
                    placeholder={t('form.email.placeholder')}
                    autoFocus={email === ''}
                    disabled={query.get('email') !== null || codeAsked}
                    value={email}
                    onChange={(event) => {
                      onEmailChange(event.target.value);
                    }}
                    error={state === 'LimitExceededException' || state === 'EmailNotFoundException'}
                  />
                  {state === 'EmailNotFoundException' && error?.message !== undefined && (
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {error.message}
                    </Typography>
                  )}
                  {state === 'LimitExceededException' && (
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {t('forgotpassword.error.toomanyattempt', {
                        defaulValue: 'Trop de tentatives, vous devez attendre 15 minutes pour réessayer',
                      })}
                    </Typography>
                  )}
                  {codeAsked && state !== 'LimitExceededException' && query.get('code') === null && (
                    <>
                      <Typography variant="body2" fontWeight="regular" sx={{mt: 1, ml: 1}}>
                        {t('signin.forgot.codeasked')}
                      </Typography>
                      <Box>
                        <Button variant="text" onClick={onAskResetCode}>
                          {t('reset.forgot.resend')}
                        </Button>
                        -{' '}
                        <Button variant="text" onClick={onResetForm}>
                          {t('reset.forgot.changeemail')}
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
                {codeAsked && state !== 'LimitExceededException' && (
                  <>
                    <Box mb={2}>
                      <TextField
                        type="text"
                        required
                        fullWidth
                        placeholder={t('form.verificationCode.placeholder')}
                        name="code"
                        autoComplete="one-time-code"
                        autoFocus={code === ''}
                        value={code}
                        disabled={query.get('code') !== null}
                        onChange={(event) => {
                          setResetState((state) => ({...state, code: event.target.value}));
                        }}
                        error={state === 'ExpiredCodeException' && error !== undefined}
                      />
                      {state === 'ExpiredCodeException' && error?.message !== undefined && (
                        <>
                          <Typography variant="body2" fontWeight="bold" color="error">
                            {error.message}
                          </Typography>
                          <Button variant="text" onClick={onAskResetCode}>
                            {t('reset.forgot.requestanewone')}
                          </Button>
                        </>
                      )}
                    </Box>
                    <Box mb={2}>
                      <TextField
                        required
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('form.password.placeholder')}
                        autoComplete="new-password"
                        autoFocus={code !== '' && email !== ''}
                        value={newPassword}
                        onChange={(event) => {
                          setResetState((state) => ({...state, newPassword: event.target.value}));
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <IconButton
                                aria-label={t('form.password.togglevisibility.arialabel')}
                                sx={{p: 0}}
                                onClick={() =>
                                  setResetState((state) => ({...state, showPassword: !state.showPassword}))
                                }
                                size="small"
                              >
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            ),
                          },
                        }}
                        error={state === 'InvalidPasswordException' && error !== undefined}
                      />
                      {state === 'InvalidPasswordException' && error?.message !== undefined && (
                        <Typography variant="body2" fontWeight="bold" color="error">
                          {error.message}
                        </Typography>
                      )}
                    </Box>
                    <PasswordChecker
                      password={newPassword}
                      repeatPassword={newPassword}
                      onPasswordInvalid={() => {
                        setResetState((state) => ({...state, invalidPassword: true}));
                      }}
                      onPasswordValid={() => {
                        setResetState((state) => ({...state, invalidPassword: undefined}));
                      }}
                    />
                  </>
                )}
                <Box my={2}>
                  {codeAsked ? (
                    state !== 'LimitExceededException' && (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={invalidPassword || code.trim().length === 0 || state === 'loading'}
                        onClick={onSubmitNewPassword}
                      >
                        {state === 'loading' ? <CircularProgress /> : t('forgotpassword.savenewpassword.btn')}
                      </Button>
                    )
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={onAskResetCode}
                      disabled={state === 'loading'}
                    >
                      {state === 'loading' ? <CircularProgress /> : t('forgotpassword.verificationcode.askbtn')}
                    </Button>
                  )}
                </Box>
                <Typography>
                  {t('signin.forgot.backtologin')}{' '}
                  <Button variant="text" component={Link} state={{email}} to="/signin">
                    {t('signin.forgot.signbtn')}
                  </Button>
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
